import { AxiosResponse } from 'axios';
import {
  all,
  AllEffect,
  call,
  CallEffect,
  ForkEffect,
  put,
  PutEffect,
  takeLatest,
} from 'redux-saga/effects';
import {
  requestApiSaga,
  retryRequestApiSaga,
} from 'b2b-apps/shop/common/redux/api/sagas';
import { reportError } from 'b2b-apps/shop/common/redux/errors/actionCreators';
import { parseError } from 'b2b-apps/shop/common/utils/api/error';
import {
  MY_ACCOUNT_MESSAGE,
  showMyAccountToast,
} from 'b2b-apps/shop/myAccount/common/utils/messages';
import { User } from 'b2b-apps/shop/myAccount/users/api/User';
import { UserType } from 'b2b-apps/shop/myAccount/users/redux/user/types';
import {
  CreateUserAction,
  FetchUserAction,
  RemoveUserAction,
  UpdateUserAction,
} from 'b2b-common/core/account/api/User.types';
import { fetchUserFulfilled, fetchUserRejected } from './actionCreators';
import {
  CREATE_USER,
  FETCH_USER,
  REMOVE_USER,
  UPDATE_USER,
} from './actionTypes';

function* createUserSaga(action: CreateUserAction): Generator<CallEffect | PutEffect, any, any> {
  const { data, callback } = action.payload;

  try {
    yield call(requestApiSaga, User.create(data));
    showMyAccountToast(MY_ACCOUNT_MESSAGE.successCreateUser);
    callback(true);
  } catch (error) {
    showMyAccountToast(MY_ACCOUNT_MESSAGE.errorCreateUser);
    callback(false);
    yield put(reportError(action.type, error));
  }
}

function* fetchUserSaga(action: FetchUserAction): Generator<CallEffect | PutEffect, any, AxiosResponse<UserType>> {
  const { accountId } = action.payload;

  try {
    const response = yield call(retryRequestApiSaga, User.fetch(accountId));
    yield put(fetchUserFulfilled(accountId, response.data));
  } catch (error) {
    yield put(fetchUserRejected(accountId, parseError(error)));
    yield put(reportError(action.type, error, action.payload));
  }
}

function* updateUserSaga(action: UpdateUserAction): Generator<CallEffect | PutEffect, any, any> {
  const { accountId, data, callback } = action.payload;

  try {
    yield call(requestApiSaga, User.update(accountId, data));
    showMyAccountToast(MY_ACCOUNT_MESSAGE.successUpdateUser);
    callback(true);
  } catch (error) {
    showMyAccountToast(MY_ACCOUNT_MESSAGE.errorUpdateUser);
    callback(false);
    yield put(
      reportError(action.type, error, { accountId }),
    );
  }
}

function* removeUserSaga(action: RemoveUserAction): Generator<CallEffect | PutEffect, any, any> {
  const { accountId, callback } = action.payload;

  try {
    yield call(requestApiSaga, User.remove(accountId));
    showMyAccountToast(MY_ACCOUNT_MESSAGE.successRemoveUser);
    callback(true);
  } catch (error) {
    showMyAccountToast(MY_ACCOUNT_MESSAGE.errorRemoveUser);
    callback(false);
    yield put(
      reportError(action.type, error, { accountId }),
    );
  }
}

function* userSaga(): Generator<AllEffect<ForkEffect>, void> {
  yield all([
    takeLatest(CREATE_USER, createUserSaga),
    takeLatest(FETCH_USER, fetchUserSaga),
    takeLatest(UPDATE_USER, updateUserSaga),
    takeLatest(REMOVE_USER, removeUserSaga),
  ]);
}

export { userSaga };
